/** ゲーム定数・謎データ（SSoT） */

export const GAME = {
  title: 'Room Escape',
  walkSpeed: 3.2,
  runSpeed: 5.4,
  playerHeight: 1.65,
  playerRadius: 0.28,
  eyeHeight: 1.55,
  gravity: 18,
  jumpForce: 5.5,
  mouseSensitivityBase: 0.0022,
  interactDistance: 3.2,
  footstepInterval: 0.42,
} as const;

/** 謎の正解 */
export const PUZZLES = {
  clockHour: 3,
  clockMinute: 15,
  /** 金庫: 絵画「48」+ 時計「15」 */
  safeCode: '4815',
  /** 本棚の色順 */
  shelfOrder: ['red', 'blue', 'green', 'yellow'] as const,
  shelfLabels: { red: '赤', blue: '青', green: '緑', yellow: '黄' } as const,
  /** ピアノ: ド・ミ・ソ */
  pianoMelody: ['C', 'E', 'G'] as const,
  /** バルブ: 水→油→ガス */
  valveOrder: ['water', 'oil', 'gas'] as const,
  /** 配線接続順: 青→黄→赤（インデックス） */
  wireOrder: [0, 1, 2] as const,
  /** 圧力計: 楽譜「Etude VII」→ 7 */
  pressureTarget: 7,
  /** 鏡の目標角度段階 (0-3) */
  mirrorTargets: [1, 2, 0] as const,
  /** 玄関 */
  exitCode: '729',
} as const;

export type ItemId =
  | 'copper_key'
  | 'blank_paper'
  | 'revealed_note'
  | 'cipher_note'
  | 'basement_key'
  | 'fuse'
  | 'silver_key'
  | 'wiring_diagram'
  | 'oil_lantern'
  | 'master_key';

export interface ItemDef {
  id: ItemId;
  name: string;
  description: string;
  icon: string;
}

export const ITEMS: Record<ItemId, ItemDef> = {
  copper_key: {
    id: 'copper_key',
    name: '銅の鍵',
    description: '古びた銅の鍵。書斎の机に合いそうだ。',
    icon: '銅',
  },
  blank_paper: {
    id: 'blank_paper',
    name: '白紙の羊皮紙',
    description: '真っ白だが、熱を通すと文字が浮かぶ種類らしい。',
    icon: '紙',
  },
  revealed_note: {
    id: 'revealed_note',
    name: '浮かび上がったメモ',
    description: '「本棚は 赤→青→緑→黄」と読める。',
    icon: '焼',
  },
  cipher_note: {
    id: 'cipher_note',
    name: '暗号メモ',
    description: '「絵画の数字と、時を刻む分を繋げ。ただし金庫は《言葉》を先に知れ」',
    icon: '暗',
  },
  basement_key: {
    id: 'basement_key',
    name: '鉄の鍵',
    description: '重厚な鉄鍵。地下へ続く扉用らしい。',
    icon: '鉄',
  },
  fuse: {
    id: 'fuse',
    name: 'ヒューズ',
    description: '配電盤に差し込む予備ヒューズ。',
    icon: '電',
  },
  silver_key: {
    id: 'silver_key',
    name: '銀の鍵',
    description: '細い銀鍵。キッチンの戸棚に合いそうだ。',
    icon: '銀',
  },
  wiring_diagram: {
    id: 'wiring_diagram',
    name: '配線図',
    description: '青→黄→赤の順に結線せよ、と記されている。',
    icon: '図',
  },
  oil_lantern: {
    id: 'oil_lantern',
    name: 'オイルランタン',
    description: '油が満たされたランタン。隠し部屋の火床に使えそうだ。',
    icon: '灯',
  },
  master_key: {
    id: 'master_key',
    name: 'マスターキー',
    description: '玄関の大扉を開ける、装飾入りの鍵。',
    icon: '主',
  },
};

/** 進行に応じたヒント（stageOrder と対応） */
export const HINTS: { when: string; text: string }[] = [
  { when: 'start', text: 'エントランスの絵画と置き手紙から始めよう。' },
  { when: 'saw_painting', text: 'III:XV と「48」。リビングの置時計を合わせよう。' },
  { when: 'clock_done', text: '銅の鍵で書斎の机を開ける。羊皮紙は熱に反応するかも。' },
  { when: 'desk_opened', text: '白紙を暖炉で炙ると、本棚の色順が現れる。' },
  { when: 'paper_revealed', text: '書斎の本棚を 赤→青→緑→黄 の順に並べ替えよう。' },
  { when: 'shelf_done', text: '金庫のパスコードは絵画の「48」と時計の「15」。' },
  { when: 'safe_opened', text: 'ダイニングの楽譜を読み、リビングのピアノで旋律を弾こう。' },
  { when: 'piano_done', text: '銀の鍵でキッチンの戸棚を開ける。' },
  { when: 'cabinet_opened', text: '冷蔵庫のメモどおり、バルブを 水→油→ガス の順で。' },
  { when: 'valves_done', text: '地下へ。圧力計は楽譜の「Etude VII」がヒント。' },
  { when: 'pressure_ok', text: 'ヒューズ装着後、配線図どおり 青→黄→赤 の順で結線。' },
  { when: 'power_restored', text: '隠し部屋でランタンを火床に灯し、鏡で光を導こう。' },
  { when: 'lamp_lit', text: '鏡を 90° / 180° / 0° に合わせ、紋章の数字を見る。' },
  { when: 'mirrors_done', text: 'マスターキーまたは 729 で玄関から脱出。' },
];

export const OBJECTIVES: { when: string; text: string }[] = [
  { when: 'start', text: '館を探索し、手がかりを集めよう' },
  { when: 'saw_painting', text: 'リビングの置時計を 3:15 に合わせる' },
  { when: 'clock_done', text: '銅の鍵で書斎の机を開ける' },
  { when: 'desk_opened', text: '羊皮紙を暖炉で炙り、隠された文字を読む' },
  { when: 'paper_revealed', text: '本棚を正しい色順に並べ替える' },
  { when: 'shelf_done', text: '書斎の金庫を開ける（4桁）' },
  { when: 'safe_opened', text: '楽譜を手がかりにピアノを演奏する' },
  { when: 'piano_done', text: '銀の鍵でキッチン戸棚を開ける' },
  { when: 'cabinet_opened', text: 'キッチンのバルブを正しい順で操作' },
  { when: 'valves_done', text: '地下室の圧力計を正しい値に合わせる' },
  { when: 'pressure_ok', text: '配電盤で電力を復旧する' },
  { when: 'power_restored', text: '隠し部屋でランタンを灯す' },
  { when: 'lamp_lit', text: '三面鏡で光を紋章へ導く' },
  { when: 'mirrors_done', text: '玄関ドアから脱出する' },
];

/**
 * 部屋レイアウト（中心 + 幅奥行）。+Z=南, +X=東
 */
export const LAYOUT = {
  floorY: 0,
  ceilingY: 3.2,
  wallThickness: 0.25,
  rooms: {
    entrance: { x: 0, z: 0, w: 8, d: 8 },
    living: { x: 9, z: 0, w: 10, d: 10 },
    study: { x: 9, z: 9, w: 10, d: 8 },
    kitchen: { x: 0, z: 8, w: 8, d: 8 },
    /** リビング東隣 */
    dining: { x: 18, z: 0, w: 8, d: 8 },
    basement: { x: -9, z: 0, w: 10, d: 8 },
    hidden: { x: -9, z: 8, w: 10, d: 8 },
  },
} as const;
